import { AppKey } from "@src/apps/manifest";
import {
  VITE_ACTIVITY_LOGS_BASE_URL,
  VITE_APP,
  VITE_ARCADIA_BASE_URL,
  VITE_AUTH_SERVICE_BASE_URL,
  VITE_BACKPACK_URL,
  VITE_EMAIL_SERVICE_BASE_URL,
  VITE_LEDGER_BASE_URL,
  VITE_MARKETPLACE_BASE_URL,
  VITE_NOTIFICATION_SERVICE_BASE_URL,
  VITE_PORTABLE_DOCUMENT_FORMAT_BASE_URL,
  VITE_REPORTER_BASE_URL,
  VITE_REWARDS_SERVICE_BASE_URL,
  VITE_SITE_SERVICE_BASE_URL,
  VITE_TENANTS_AND_SPACES_BASE_URL,
  VITE_UBIN_PORTAL_API_URL,
  VITE_UPLOAD_BASE_URL,
  VITE_UTILITIES_BASE_URL,
} from "@src/env";

const urlEnvKeysByApp = {
  backpack: [
    "VITE_BACKPACK_URL",
    "VITE_MARKETPLACE_BASE_URL",
    "VITE_PORTABLE_DOCUMENT_FORMAT_BASE_URL",
    "VITE_AUTH_SERVICE_BASE_URL",
    "VITE_EMAIL_SERVICE_BASE_URL",
    "VITE_NOTIFICATION_SERVICE_BASE_URL",
    "VITE_REWARDS_SERVICE_BASE_URL",
    "VITE_REPORTER_BASE_URL",
    "VITE_LEDGER_BASE_URL",
    "VITE_UPLOAD_BASE_URL",
    "VITE_SITE_SERVICE_BASE_URL",
    "VITE_TENANTS_AND_SPACES_BASE_URL",
    "VITE_UTILITIES_BASE_URL",
    "VITE_ACTIVITY_LOGS_BASE_URL",
    "VITE_ARCADIA_BASE_URL",
  ],
  ubin_portal: ["VITE_UBIN_PORTAL_API_URL"],
} satisfies Record<AppKey, readonly string[]>;

const stagingDomains = ["bpn-staging.com", "ubin-staging.ai"];
const prodDomains = ["backpacknetworks.com", "ubin.ai"];

const matchesDomain = (url: string, domains: string[]) =>
  domains.some((domain) => url.includes(domain));

// narrowing function, because env-var .asString() types every variable as `string` even when it may not be set
const definedUrls = (source: RawUrlEnvs, app: AppKey) =>
  urlEnvKeysByApp[app]
    .map((key) => source[key])
    .filter((value): value is string => typeof value === "string" && !!value);

export const createAllUrlEnvsMap = (
  source: RawUrlEnvs,
  app: AppKey = VITE_APP
) =>
  urlEnvKeysByApp[app].reduce<Record<string, string>>((result, key) => {
    const value = source[key];
    return typeof value === "string" && value
      ? { ...result, [key]: value }
      : result;
  }, {});

export const createEnvIsStaging = (
  source: RawUrlEnvs,
  app: AppKey = VITE_APP
) => definedUrls(source, app).some((url) => matchesDomain(url, stagingDomains));

export const createEnvIsProd = (source: RawUrlEnvs, app: AppKey = VITE_APP) =>
  definedUrls(source, app).some((url) => matchesDomain(url, prodDomains));

export const createConsistentEnv = (
  source: RawUrlEnvs,
  app: AppKey = VITE_APP
) => {
  const urls = definedUrls(source, app);
  if (urls.length === 0) {
    return false;
  }
  return (
    urls.every((url) => matchesDomain(url, stagingDomains)) ||
    urls.every((url) => matchesDomain(url, prodDomains))
  );
};

export const createDomainsFromEnv = (
  source: RawUrlEnvs,
  app: AppKey = VITE_APP
) =>
  definedUrls(source, app).reduce(
    (domains, url) => domains.add(new URL(url).hostname),
    new Set<string>()
  );

type RawUrlEnvs = { [key: string]: string | boolean | undefined };

export const urlEnvs = {
  VITE_ACTIVITY_LOGS_BASE_URL,
  VITE_ARCADIA_BASE_URL,
  VITE_AUTH_SERVICE_BASE_URL,
  VITE_BACKPACK_URL,
  VITE_EMAIL_SERVICE_BASE_URL,
  VITE_LEDGER_BASE_URL,
  VITE_MARKETPLACE_BASE_URL,
  VITE_NOTIFICATION_SERVICE_BASE_URL,
  VITE_PORTABLE_DOCUMENT_FORMAT_BASE_URL,
  VITE_REPORTER_BASE_URL,
  VITE_REWARDS_SERVICE_BASE_URL,
  VITE_SITE_SERVICE_BASE_URL,
  VITE_TENANTS_AND_SPACES_BASE_URL,
  VITE_UBIN_PORTAL_API_URL,
  VITE_UPLOAD_BASE_URL,
  VITE_UTILITIES_BASE_URL,
};
